import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import {
  deliveryAssigneeLabel,
  deliveryTypeLabel,
  fetchDeliveryArrangementsForClient,
  formatDeliveryMoney,
  summarizeDeliveryArrangements,
} from '../../lib/deliveryArrangements';
import { formatSingaporeDateTime } from '../../utils/dateTime';
import type { Client } from '../../types/crm';
import type { DeliveryArrangement } from '../../types/delivery';
import { SIDEBAR_LIST_PREVIEW, takePreview } from '../../utils/accountSidebarList';

type AccountDeliveriesPanelProps = {
  client: Client;
};

export function AccountDeliveriesPanel({ client }: AccountDeliveriesPanelProps) {
  const [rows, setRows] = useState<DeliveryArrangement[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<DeliveryArrangement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const data = await fetchDeliveryArrangementsForClient(client.id);
        if (!cancelled) setRows(data);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client.id]);

  const summary = useMemo(() => summarizeDeliveryArrangements(rows), [rows]);
  const previewRows = takePreview(rows);
  const hiddenCount = Math.max(0, rows.length - SIDEBAR_LIST_PREVIEW);

  function renderRow(row: DeliveryArrangement, inModal = false) {
    return (
      <li key={row.id} className="account-side-list__row">
        <div className="account-side-list__body">
          <button
            type="button"
            className="account-side-list__name account-side-link account-side-link--button"
            onClick={() => setDetailRow(row)}
          >
            {formatSingaporeDateTime(row.created_at)} · {row.reason}
          </button>
          <span className="account-side-muted">
            {deliveryTypeLabel(row.fulfillment_type)} ·{' '}
            {formatDeliveryMoney(row.reference_fee_amount, row.reference_fee_currency)} ref.
          </span>
        </div>
        {!inModal ? (
          <div className="account-side-list__btns">
            <button type="button" className="account-side-btn" onClick={() => setDetailRow(row)}>
              View
            </button>
          </div>
        ) : null}
      </li>
    );
  }

  return (
    <>
      <section className="account-side-card">
        <div className="account-side-card__head">
          <h2 className="account-side-card__title">Deliveries ({rows.length})</h2>
        </div>
        {loading ? (
          <p className="account-side-muted">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="account-side-muted">No delivery arrangements linked to this account.</p>
        ) : (
          <>
            <div className="account-deliveries-summary">
              <span>{summary.total} total</span>
              <span>{summary.thisYear} this year</span>
              <span>{summary.lalamoveCount} Lalamove</span>
              <span>{summary.internalCount} internal</span>
            </div>
            <ul className="account-side-list">{previewRows.map((row) => renderRow(row))}</ul>
            {hiddenCount > 0 ? (
              <button type="button" className="account-side-view-all" onClick={() => setViewAllOpen(true)}>
                View all ({rows.length})
              </button>
            ) : null}
          </>
        )}
      </section>

      <Modal
        title={`Deliveries · ${client.company_name}`}
        open={viewAllOpen}
        onClose={() => setViewAllOpen(false)}
        wide
      >
        <ul className="account-side-list account-side-list--modal">
          {rows.map((row) => renderRow(row, true))}
        </ul>
      </Modal>

      <Modal
        title="Delivery arrangement"
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
      >
        {detailRow ? (
          <dl className="delivery-detail-card__meta">
            <dt>Date</dt>
            <dd>{formatSingaporeDateTime(detailRow.created_at)}</dd>
            <dt>Type</dt>
            <dd>{deliveryTypeLabel(detailRow.fulfillment_type)}</dd>
            <dt>Assignee</dt>
            <dd>{deliveryAssigneeLabel(detailRow)}</dd>
            <dt>Reason</dt>
            <dd>{detailRow.reason}</dd>
            <dt>Document no.</dt>
            <dd>{detailRow.document_no?.trim() || '—'}</dd>
            <dt>Status</dt>
            <dd>{detailRow.status}</dd>
            <dt>Lalamove reference fee</dt>
            <dd>{formatDeliveryMoney(detailRow.reference_fee_amount, detailRow.reference_fee_currency)}</dd>
            <dt>Creator</dt>
            <dd>{detailRow.created_by}</dd>
            <dt>Requested by</dt>
            <dd>{detailRow.requested_by?.trim() || '—'}</dd>
            <dt>Issue caused by</dt>
            <dd>{detailRow.issue_caused_by?.trim() || '—'}</dd>
            {detailRow.tracking_url ? (
              <>
                <dt>Tracking</dt>
                <dd>
                  <a href={detailRow.tracking_url} target="_blank" rel="noreferrer">
                    Open tracking
                  </a>
                </dd>
              </>
            ) : null}
          </dl>
        ) : null}
      </Modal>
    </>
  );
}
